import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RefreshCw, Loader2, Search, Plus, Minus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BLUE_COIN_IMG = 'https://customer-assets-eiarnc6j.emergentagent.net/job_8f198eda-3cd9-45f6-a741-5b80db2d25c7/artifacts/n7x3phd0_image.png';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
});

const ENTRY_LABELS = {
  admin_adjust: 'Ajuste manual',
  admin_reset: 'Reset geral',
  checkin: 'Check-in',
  roleta: 'Roleta',
  campanha: 'Campanha',
  evento: 'Evento',
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch { return iso || '-'; }
};

const StatCard = ({ label, value, sub, testId }) => (
  <Card className="border-sky-100 bg-gradient-to-br from-sky-50 to-white">
    <CardContent className="p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-sky-600">{label}</p>
      <p className="text-2xl font-black text-blue-700" data-testid={testId}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
    </CardContent>
  </Card>
);

const BlueCoinsManagement = () => {
  const [stats, setStats] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [ledgerKako, setLedgerKako] = useState('');

  // ajuste manual
  const [adjKako, setAdjKako] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // reset geral
  const [resets, setResets] = useState([]);
  const [resetReason, setResetReason] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ownerFilter !== 'all') params.profile = ownerFilter;
      if (search.trim()) params.search = search.trim();
      const lparams = {};
      if (ledgerKako.trim()) lparams.kako_id = ledgerKako.trim();
      const [s, w, l, r] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/blue-coins/admin/stats`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/blue-coins/admin/wallets`, { headers: authHeaders(), params }),
        axios.get(`${BACKEND_URL}/api/blue-coins/admin/ledger`, { headers: authHeaders(), params: lparams }),
        axios.get(`${BACKEND_URL}/api/blue-coins/admin/resets`, { headers: authHeaders() }),
      ]);
      setStats(s.data); setWallets(w.data); setLedger(l.data); setResets(r.data.resets || []);
    } catch {
      toast.error('Erro ao carregar dados das Azulzinhas');
    }
    setLoading(false);
  }, [ownerFilter, search, ledgerKako]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doResetAll = async () => {
    if (resetReason.trim().length < 3) { toast.error('Descreva o motivo do reset (mín. 3 caracteres)'); return; }
    if (resetConfirm.trim().toUpperCase() !== 'ZERAR') { toast.error('Digite ZERAR no campo de confirmação'); return; }
    if (!window.confirm('🧹 RESET GERAL: TODAS as carteiras de Azulzinhas serão ZERADAS (clientes e afiliados). Cada débito fica auditado no livro-razão. Esta ação NÃO pode ser desfeita. Confirmar?')) return;
    setResetting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/blue-coins/admin/reset-all`, {
        reason: resetReason.trim(), confirm: 'ZERAR',
      }, { headers: authHeaders() });
      toast.success(`Reset concluído: ${r.data.wallets_affected} carteira(s) zeradas — ${r.data.total_zeroed.toLocaleString('pt-BR')} Azulzinhas removidas`);
      setResetReason(''); setResetConfirm('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resetar carteiras');
    }
    setResetting(false);
  };

  const doAdjust = async (sign) => {
    const amount = parseInt(adjAmount, 10);
    if (!amount || amount <= 0) { toast.error('Digite uma quantidade válida'); return; }
    if (!adjKako.trim()) { toast.error('Digite o KAKO ID'); return; }
    if (adjReason.trim().length < 3) { toast.error('Descreva o motivo (mín. 3 caracteres)'); return; }
    setAdjusting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/blue-coins/admin/adjust`, {
        kako_id: adjKako.trim(),
        amount: sign * amount, reason: adjReason.trim(),
      }, { headers: authHeaders() });
      toast.success(`${sign > 0 ? 'Creditado' : 'Debitado'}! Saldo atual: ${r.data.balance.toLocaleString('pt-BR')}`);
      setAdjAmount(''); setAdjReason('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao ajustar');
    }
    setAdjusting(false);
  };

  return (
    <div className="space-y-4" data-testid="blue-coins-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <img src={BLUE_COIN_IMG} alt="Azulzinhas" className="w-7 h-7 object-contain drop-shadow-sm" /> Azulzinhas 💙
          </h2>
          <p className="text-sm text-gray-500">
            Moeda virtual interna — todo movimento fica registrado no livro-razão (auditável).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="blue-coins-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Em circulação" value={stats.total_in_circulation.toLocaleString('pt-BR')} testId="bc-stat-circulation" />
          <StatCard label="Carteiras" value={stats.total_wallets} sub="1 carteira por KAKO ID" testId="bc-stat-wallets" />
          <StatCard label="Perfil cliente" value={stats.customer_wallets} sub="carteiras" testId="bc-stat-customers" />
          <StatCard label="Perfil afiliado" value={stats.reseller_wallets} sub="carteiras" testId="bc-stat-resellers" />
        </div>
      )}

      {/* Ajuste manual */}
      <Card className="border-sky-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Creditar / Debitar manualmente</CardTitle>
          <CardDescription className="text-xs">
            A carteira é ÚNICA por KAKO ID (cliente e afiliado compartilham). Sempre auditado: fica registrado quem ajustou, quanto e o motivo.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <Input className="w-36" placeholder="KAKO ID" value={adjKako} inputMode="numeric"
            onChange={(e) => setAdjKako(e.target.value.replace(/\D/g, ''))}
            data-testid="bc-adjust-kako-input" />
          <Input className="w-32" placeholder="Quantidade" value={adjAmount} inputMode="numeric"
            onChange={(e) => setAdjAmount(e.target.value.replace(/\D/g, ''))}
            data-testid="bc-adjust-amount-input" />
          <Input className="flex-1 min-w-[180px]" placeholder="Motivo (obrigatório)" value={adjReason}
            onChange={(e) => setAdjReason(e.target.value)} data-testid="bc-adjust-reason-input" />
          <Button size="sm" onClick={() => doAdjust(1)} disabled={adjusting}
            className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="bc-adjust-credit-button">
            <Plus className="w-4 h-4 mr-1" /> Creditar
          </Button>
          <Button size="sm" variant="outline" onClick={() => doAdjust(-1)} disabled={adjusting}
            className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="bc-adjust-debit-button">
            <Minus className="w-4 h-4 mr-1" /> Debitar
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="wallets">
        <TabsList>
          <TabsTrigger value="wallets" data-testid="bc-tab-wallets">Carteiras</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="bc-tab-ledger">Livro-razão</TabsTrigger>
        </TabsList>

        <TabsContent value="wallets" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="w-40">
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger data-testid="bc-wallets-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="customer">Clientes</SelectItem>
                  <SelectItem value="reseller">Afiliados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input className="pl-8" placeholder="Buscar por KAKO ID ou nome"
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="bc-wallets-search" />
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bc-wallets-table">
                  <thead>
                    <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                      <th className="p-3">Perfis</th>
                      <th className="p-3">KAKO ID</th>
                      <th className="p-3">Nome</th>
                      <th className="p-3 text-right">Saldo 💙</th>
                      <th className="p-3">Atualizada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallets.length === 0 ? (
                      <tr><td colSpan={5} className="p-6 text-center text-gray-400" data-testid="bc-wallets-empty">
                        Nenhuma carteira ainda — elas nascem no primeiro crédito.
                      </td></tr>
                    ) : wallets.map((w) => (
                      <tr key={w.id} className="border-b last:border-0 hover:bg-sky-50/40">
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            {(w.profiles || []).includes('customer') && (
                              <Badge variant="outline" className="border-purple-300 text-purple-700">Cliente</Badge>
                            )}
                            {(w.profiles || []).includes('reseller') && (
                              <Badge variant="outline" className="border-emerald-300 text-emerald-700">Afiliado</Badge>
                            )}
                            {(!w.profiles || w.profiles.length === 0) && <span className="text-gray-400">—</span>}
                          </div>
                        </td>
                        <td className="p-3 font-mono">{w.kako_id || '—'}</td>
                        <td className="p-3">{w.nickname || '—'}</td>
                        <td className="p-3 text-right font-black text-blue-700">
                          {Number(w.balance || 0).toLocaleString('pt-BR')}
                        </td>
                        <td className="p-3 text-xs text-gray-500">{fmtDate(w.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ledger" className="space-y-3">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input className="pl-8" placeholder="Filtrar por KAKO ID" value={ledgerKako}
              inputMode="numeric" onChange={(e) => setLedgerKako(e.target.value.replace(/\D/g, ''))}
              data-testid="bc-ledger-kako-filter" />
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bc-ledger-table">
                  <thead>
                    <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                      <th className="p-3">Data</th>
                      <th className="p-3">Dono</th>
                      <th className="p-3">Motivo</th>
                      <th className="p-3 text-right">Quantia</th>
                      <th className="p-3 text-right">Saldo após</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.length === 0 ? (
                      <tr><td colSpan={6} className="p-6 text-center text-gray-400" data-testid="bc-ledger-empty">
                        Nenhuma movimentação registrada ainda.
                      </td></tr>
                    ) : ledger.map((e) => (
                      <tr key={e.id} className="border-b last:border-0 hover:bg-sky-50/40">
                        <td className="p-3 text-xs">{fmtDate(e.created_at)}</td>
                        <td className="p-3 text-xs">
                          <span className="font-mono">{e.kako_id || '—'}</span>
                        </td>
                        <td className="p-3 text-xs">
                          {ENTRY_LABELS[e.entry_type] || e.entry_type}
                          {e.description ? ` · ${e.description}` : ''}
                        </td>
                        <td className={`p-3 text-right font-bold ${e.direction === 'credit' ? 'text-sky-600' : 'text-rose-500'}`}>
                          {e.direction === 'credit' ? '+' : '−'}{Number(e.amount).toLocaleString('pt-BR')}
                        </td>
                        <td className="p-3 text-right">{e.balance_after !== null ? Number(e.balance_after).toLocaleString('pt-BR') : '—'}</td>
                        <td className="p-3">
                          {e.status === 'completed'
                            ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OK</Badge>
                            : <Badge className="bg-rose-100 text-rose-600 hover:bg-rose-100">sem saldo</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Zona de perigo — reset geral */}
      <Card className="border-red-300" data-testid="bc-reset-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Zona de Perigo — Zerar todas as carteiras
          </CardTitle>
          <CardDescription className="text-xs">
            Zera o saldo de TODAS as carteiras de <b>Azulzinhas 💙</b> de uma vez — útil para reiniciar um período ou ativar Azulzinhas só em eventos. Cada carteira recebe um débito auditado no livro-razão e o reset fica registrado no histórico abaixo com quem fez, quando e o motivo.
            <span className="block mt-1 font-semibold text-emerald-700">✔️ Afeta SOMENTE Azulzinhas: a carteira de moedas amarelas dos afiliados e qualquer outra carteira do sistema ficam 100% intactas.</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Input placeholder="Motivo do reset (ex.: fim da edição de junho)" value={resetReason}
                onChange={(e) => setResetReason(e.target.value)} maxLength={200}
                data-testid="bc-reset-reason-input" />
            </div>
            <Input className="w-36" placeholder='Digite "ZERAR"' value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              data-testid="bc-reset-confirm-input" />
            <Button variant="destructive" size="sm" disabled={resetting} onClick={doResetAll}
              data-testid="bc-reset-button">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
              Zerar Carteiras
            </Button>
          </div>

          {resets.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">📜 Histórico de resets</p>
              <div className="space-y-1">
                {resets.map((r) => (
                  <div key={r.id} className="text-xs text-gray-600 rounded border bg-red-50/50 px-2 py-1.5 flex flex-wrap gap-x-3 gap-y-0.5"
                    data-testid={`bc-reset-item-${r.id}`}>
                    <span className="font-medium">{fmtDate(r.created_at)}</span>
                    <span>🧹 {r.wallets_affected} carteira(s)</span>
                    <span className="text-red-600 font-semibold">-{(r.total_zeroed || 0).toLocaleString('pt-BR')} 💙</span>
                    <span className="text-gray-500 italic">"{r.reason}"</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlueCoinsManagement;
